import React, { Fragment } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Skeleton } from '@mui/lab'
import { Card, Typography, useAnalyticsPageView, useEffectOnce, usePathParam } from '@chilecompra/react-kit'
import { USER_TYPE } from '../../config/settings/constant.js'
import { PUBLIC_URL } from '../../config/settings/environment'
import {
  onGetTemporaryUnionCertificateThunk,
  onGetTemporaryUnionsSummaryThunk
} from './TemporaryUnionsSection.actions.js'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'
import {
  AbilityCertificateLink,
  Column,
  Content,
  ErrorIcon,
  ErrorText,
  MessageColumn,
  ProcessesColumn,
  ProvidersInfoColumn,
  ProvidersInfoRow,
  Row,
  SecondaryText,
  Separator,
  StatusColumn,
  SuccessIcon,
  SuccessText,
  UnderlineText,
  WarningIcon
} from './TemporaryUnionsSection.styles.jsx'

/**
 * The temporary unions section container.
 */
export const TemporaryUnionsSection = () => {
  const { userType } = useAuthorization()
  const dispatch = useDispatch()
  const dni = usePathParam('rut')
  const { error, loading, unions } = useSelector(state => state.temporaryUnions)

  useEffectOnce(() => {
    if (!loading) return

    dispatch(onGetTemporaryUnionsSummaryThunk({ dni }))
  })

  const ConditionalWrapper = ({ condition, wrapper, children }) => (condition ? wrapper(children) : children)

  const getStatusIcon = (status, wrapper = null) => {
    if (status === 'Habil') {
      return (
        <ConditionalWrapper condition={wrapper} wrapper={wrapper}>
          <SuccessIcon />
          <SuccessText fontWeight="bold" variant="body2">
            Hábil
          </SuccessText>
        </ConditionalWrapper>
      )
    } else if (status === 'Inhabil') {
      return (
        <ConditionalWrapper condition={wrapper} wrapper={wrapper}>
          <ErrorIcon />
          <ErrorText fontWeight="bold" variant="body2">
            Inhábil
          </ErrorText>
        </ConditionalWrapper>
      )
    } else {
      return (
        <ConditionalWrapper condition={wrapper} wrapper={wrapper}>
          <WarningIcon />
          <SecondaryText fontWeight="bold" variant="body2">
            Sin información
          </SecondaryText>
        </ConditionalWrapper>
      )
    }
  }

  const handleDownload = bidID => () => {
    if (bidID !== undefined) {
      dispatch(onGetTemporaryUnionCertificateThunk({ bidID }))
    }
  }

  useAnalyticsPageView('/ficha/uniones-temporales')
  return error && error?.reason !== 'SOMETHING_WENT_WRONG' ? null : (
    <Content>
      <Typography variant="h4" fontWeight="bold" padding="0 0 4px">
        Unión temporal de proveedores
      </Typography>
      <Typography variant="subtitle" color="black3" padding="0 0 6px">
        Estas son las uniones temporales en las que esta empresa ha participado durante los últimos 24 meses. El estado
        de habilidad indicado corresponde al vigente al momento en que se generó la unión temporal.
      </Typography>
      {unions.length !== 0 ? (
        unions.map(temporaryUnion => (
          <Card key={temporaryUnion.ID} color="blank" enableBorder margin="24px 0 0" padding="21px 24px 8px">
            {userType !== USER_TYPE.PUBLIC && (
              <Typography variant="body2">
                <AbilityCertificateLink onClick={handleDownload(temporaryUnion.bidID)}>
                  Generar certificado de habilidad
                </AbilityCertificateLink>
              </Typography>
            )}
            <Row>
              <Column width="150px">
                <Typography fontWeight="bold" variant="body2">
                  ID UTP
                </Typography>
              </Column>
              <Column width="auto">
                <Typography variant="body2">{temporaryUnion.ID}</Typography>
              </Column>
            </Row>
            <Row>
              <Column width="150px">
                <Typography fontWeight="bold" padding="0 0 3px" margin="10px 0" variant="body2">
                  Estado de habilidad
                </Typography>
              </Column>
              {getStatusIcon(temporaryUnion.abilityStatus.status, children => (
                <StatusColumn width="auto">{children}</StatusColumn>
              ))}
            </Row>
            {temporaryUnion.abilityStatus.description !== '' || temporaryUnion.abilityStatus.reason !== '' ? (
              <Row>
                <Column width="150px" />
                <MessageColumn width="auto">
                  {temporaryUnion.abilityStatus.description !== '' ? (
                    <SecondaryText variant="body2">{temporaryUnion.abilityStatus.description}</SecondaryText>
                  ) : null}
                  {temporaryUnion.abilityStatus.reason !== '' ? (
                    <SecondaryText variant="body2">Motivo: {temporaryUnion.abilityStatus.reason}</SecondaryText>
                  ) : null}
                </MessageColumn>
              </Row>
            ) : null}
            {temporaryUnion.suppliers.length > 0 ? (
              <Row>
                <Column width="100%">
                  <Typography fontWeight="bold" padding="20px 0 3px" variant="body2">
                    Proveedores que participan de esta Unión Temporal
                  </Typography>
                  {temporaryUnion.suppliers.map(supplier => (
                    <Row key={supplier.ID}>
                      <Column width="135px">
                        <SecondaryText margin="10px 0" variant="body2">
                          {supplier.dni}
                        </SecondaryText>
                      </Column>
                      <ProvidersInfoColumn width="75%">
                        <ProvidersInfoRow alignItems="center">
                          <UnderlineText margin="10px 0" variant="body2" padding="0 20px 0">
                            {supplier.businessName}
                          </UnderlineText>
                          {getStatusIcon(supplier.status)}
                        </ProvidersInfoRow>
                      </ProvidersInfoColumn>
                    </Row>
                  ))}
                </Column>
              </Row>
            ) : null}
            {temporaryUnion.processes.length > 0 ? (
              <Row>
                <Column width="100%">
                  <Typography fontWeight="bold" padding="15px 0 3px" variant="body2">
                    Procesos en los que ha participado esta Unión Temporal
                  </Typography>
                  {temporaryUnion.processes.map((process, index, arr) => (
                    <Fragment key={process.ID}>
                      <Row>
                        <Column width="135px">
                          <SecondaryText margin="10px 0" variant="body2">
                            {process.ID}
                          </SecondaryText>
                        </Column>
                        <ProcessesColumn>
                          <Row alignItems="center">
                            <Typography margin="10px 0" variant="body2" padding="0 20px 0">
                              {process.description}
                            </Typography>
                          </Row>
                        </ProcessesColumn>
                      </Row>
                      {index < arr.length - 1 ? <Separator /> : null}
                    </Fragment>
                  ))}
                </Column>
              </Row>
            ) : null}
          </Card>
        ))
      ) : loading ? (
        <Skeleton variant="rect" height={200} />
      ) : (
        <Card color="blank" enableBorder margin="24px 0 0" padding="21px 24px">
          <Row alignItems="center" justifyContent="center" direction="column" height="200px">
            <img src={`${PUBLIC_URL}/assets/images/notFound.svg`} width={60} />
            <Typography variant="body2" color="black3" width="40%" align="center" margin="20px 0 0">
              Esta empresa proveedora no ha participado de uniones temporales en Mercado Público en los últimos 24
              meses.
            </Typography>
          </Row>
        </Card>
      )}
    </Content>
  )
}
